/**
 * Sparrow LaunchPad — core design tokens.
 *
 * Single source of truth for brand colors, semantic surfaces, typography
 * stacks and shape values consumed by `myTheme.ts` and component overrides.
 *
 * Values mirror the "Sparrow Launchpad — Twigs" design spec: DM Sans type,
 * teal primary (#00828D light / #2CC9C0 dark), a violet accent, warm-neutral
 * text, and — unlike the previous direction — a LIGHT sidebar in light mode.
 */

/** Brand color scale, anchored on the SurveySparrow logo teal (#00828D). */
export const brand = {
  teal900: '#04353B',
  teal800: '#00555C', // primary-700 (light)
  teal700: '#006B74', // primary-hover (light)
  teal600: '#00828D', // primary (light) — matches the logo mark
  teal500: '#0E9AA3',
  teal400: '#2CC9C0', // primary (dark)
  teal300: '#4FD8D0', // primary-hover (dark)
  teal200: '#7FE3DC', // primary-700 (dark)
  amber500: '#B47409', // warm accent — CTAs, highlights
  amber400: '#FBBF24',
  violet500: '#7158F5', // accent (light)
  violet300: '#A99CFB', // accent (dark)
} as const;

/**
 * Status colors, aligned to the design's semantic chips (pos/neg/warn/info).
 * Light values keep ≥4.5:1 contrast on white surfaces.
 */
export const status = {
  light: {
    ok: '#4C8425',
    warning: '#B47409',
    error: '#D14729',
    running: '#7158F5',
    pending: '#B47409',
    aborted: '#8A98A0',
  },
  dark: {
    ok: '#A8D291',
    warning: '#FBBF24',
    error: '#FFB4A1',
    running: '#A99CFB',
    pending: '#FBBF24',
    aborted: '#6B777C',
  },
} as const;

/** Lifecycle badge colors (catalog "Lifecycle" column + entity chips). */
export interface LifecycleColor {
  background: string;
  color: string;
  border: string;
}

export const lifecycleColors: Record<
  'light' | 'dark',
  Record<string, LifecycleColor>
> = {
  light: {
    production: { background: '#EFF7E9', color: '#4C8425', border: '#D3E9C2' },
    staging: { background: '#FEF3E2', color: '#B47409', border: '#FBDBA7' },
    experimental: { background: '#F3F3FF', color: '#5B45D6', border: '#DDD8FB' },
    deprecated: { background: '#FDEDE8', color: '#D14729', border: '#F7CFC4' },
    development: { background: '#E8F1FD', color: '#1D4ED8', border: '#C4DCFA' },
    default: { background: '#F8F8F8', color: '#4E596C', border: '#E6E9EC' },
  },
  dark: {
    production: {
      background: 'rgba(103,176,52,0.15)',
      color: '#A8D291',
      border: 'rgba(103,176,52,0.35)',
    },
    staging: {
      background: 'rgba(251,171,36,0.14)',
      color: '#FBBF24',
      border: 'rgba(251,171,36,0.35)',
    },
    experimental: {
      background: 'rgba(151,140,249,0.15)',
      color: '#A99CFB',
      border: 'rgba(151,140,249,0.35)',
    },
    deprecated: {
      background: 'rgba(246,86,51,0.15)',
      color: '#FFB4A1',
      border: 'rgba(246,86,51,0.35)',
    },
    development: {
      background: 'rgba(96,165,250,0.15)',
      color: '#93C5FD',
      border: 'rgba(96,165,250,0.35)',
    },
    default: {
      background: '#1E272C',
      color: '#9FADB2',
      border: '#2A343A',
    },
  },
};

/** A semantic status color paired with its tinted background (chips/badges). */
export interface StatusTone {
  color: string;
  bg: string;
}

/** Semantic surface/text tokens per mode. */
export interface ModeTokens {
  /* Surfaces */
  canvas: string;
  surface: string;
  /** Second-level surface (inputs, hover fills). Design: --c-surface2. */
  surfaceSubtle: string;
  /* Text */
  text: string;
  textMuted: string;
  textSubtle: string;
  /* Lines */
  border: string;
  /** Stronger hairline for scrollbars / dividers. Design: --c-border-strong. */
  borderStrong: string;
  divider: string;
  zebra: string;
  rowHover: string;
  focusRing: string;
  /* Primary (teal) */
  primary: string;
  primaryHover: string;
  /** Deepest readable primary — used for text on tinted primary fills. */
  primary700: string;
  /** Subtle primary tint for icon chips / active nav backgrounds. */
  primary50: string;
  /** Primary-tinted border for outlined primary surfaces. */
  primaryBorder: string;
  primaryContrast: string;
  /* Accent (violet) */
  accent: string;
  accent50: string;
  /* Semantic tones */
  positive: StatusTone;
  negative: StatusTone;
  warning: StatusTone;
  info: StatusTone;
  /* Sidebar chrome */
  sidebar: string;
  sidebarBorder: string;
  /* Elevation */
  cardShadow: string;
  cardShadowLg: string;
}

export const light: ModeTokens = {
  canvas: '#F4F6F7',
  surface: '#FFFFFF',
  surfaceSubtle: '#F8F8F8',
  text: '#363A43',
  textMuted: '#4E596C',
  textSubtle: '#8A98A0',
  border: '#E6E9EC',
  borderStrong: '#D3D9DE',
  divider: '#E6E9EC',
  zebra: 'rgba(54, 58, 67, 0.018)',
  rowHover: '#F8F8F8',
  focusRing: 'rgba(0, 130, 141, 0.28)',
  primary: brand.teal600,
  primaryHover: brand.teal700,
  primary700: brand.teal800,
  primary50: '#E6F5F6',
  primaryBorder: '#BFE4E7',
  primaryContrast: '#FFFFFF',
  accent: brand.violet500,
  accent50: '#F3F3FF',
  positive: { color: '#4C8425', bg: '#EFF7E9' },
  negative: { color: '#D14729', bg: '#FDEDE8' },
  warning: { color: '#B47409', bg: '#FEF3E2' },
  info: { color: '#1D4ED8', bg: '#E8F1FD' },
  sidebar: '#FFFFFF',
  sidebarBorder: '#E6E9EC',
  cardShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  cardShadowLg: '0 12px 30px rgba(0, 0, 0, 0.10)',
};

export const dark: ModeTokens = {
  canvas: '#0E1316',
  surface: '#161C20',
  surfaceSubtle: '#1E272C',
  text: '#EAF1F2',
  textMuted: '#9FADB2',
  textSubtle: '#6B777C',
  border: '#2A343A',
  borderStrong: '#3A464D',
  divider: '#2A343A',
  zebra: 'rgba(234, 241, 242, 0.02)',
  rowHover: '#1E272C',
  focusRing: 'rgba(44, 201, 192, 0.35)',
  primary: brand.teal400,
  primaryHover: brand.teal300,
  primary700: brand.teal200,
  primary50: 'rgba(44, 201, 192, 0.13)',
  primaryBorder: 'rgba(44, 201, 192, 0.30)',
  primaryContrast: '#04252A',
  accent: brand.violet300,
  accent50: 'rgba(151, 140, 249, 0.15)',
  positive: { color: '#A8D291', bg: 'rgba(103, 176, 52, 0.15)' },
  negative: { color: '#FFB4A1', bg: 'rgba(246, 86, 51, 0.15)' },
  warning: { color: '#FBBF24', bg: 'rgba(251, 171, 36, 0.14)' },
  info: { color: '#93C5FD', bg: 'rgba(96, 165, 250, 0.15)' },
  sidebar: '#11171A',
  sidebarBorder: '#2A343A',
  cardShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
  cardShadowLg: '0 14px 34px rgba(0, 0, 0, 0.5)',
};

/**
 * Sidebar chrome. In light mode the nav is a WHITE surface (per the Twigs
 * spec) so it reads as part of the page; in dark mode it is a near-black
 * panel. The teal indicator and tinted active state carry the brand in both.
 */
export const navigation = {
  light: {
    background: light.sidebar,
    submenuBackground: light.surfaceSubtle,
    indicator: brand.teal600,
    color: light.textMuted,
    selectedColor: light.primary700,
    hoverBackground: light.surfaceSubtle,
    border: light.sidebarBorder,
    sectionLabel: light.textSubtle,
  },
  dark: {
    background: dark.sidebar,
    submenuBackground: '#0B1114',
    indicator: brand.teal400,
    color: dark.textMuted,
    selectedColor: dark.primary700,
    hoverBackground: dark.surfaceSubtle,
    border: dark.sidebarBorder,
    sectionLabel: dark.textSubtle,
  },
} as const;

/** rgba() of the indicator teal (brand.teal400, #2CC9C0). */
export const tealAlpha = (alpha: number): string =>
  `rgba(44, 201, 192, ${alpha})`;

/** rgba() of the primary teal (brand.teal600, #00828D). */
export const deepTealAlpha = (alpha: number): string =>
  `rgba(0, 130, 141, ${alpha})`;

/** Typography stacks. DM Sans + JetBrains Mono are bundled via @fontsource. */
export const fonts = {
  sans: '"DM Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", "Fira Code", ui-monospace, Menlo, Consolas, monospace',
} as const;

/** Shape scale. */
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 14,
} as const;

/** Elevation — soft, diffuse shadows instead of harsh MUI defaults. */
export const shadow = {
  card: light.cardShadow,
  cardDark: dark.cardShadow,
  raised: light.cardShadowLg,
  raisedDark: dark.cardShadowLg,
} as const;
